import { useState } from "react";
import { toast } from "sonner";
import type { UseFormSetError, FieldValues } from "react-hook-form";
import { useUtmParams } from "@/hooks/use-utm-params";
export interface SubmissionError extends Error {
  validationErrors?: Record<string, string | string[]>;
}
interface UseFormSubmissionOptions<TFieldValues extends FieldValues = any, TResponse = any> {
  /**
   * The API endpoint to send the POST request to.
   * Defaults to `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/enquiry-details`
   */
  endpoint?: string;
  /**
   * React Hook Form's setError function to automatically set validation errors on fields.
   */
  setError?: UseFormSetError<TFieldValues>;
  /**
   * Optional callback triggered on successful submission.
   */
  onSuccess?: (data: TResponse) => void;
  /**
   * Optional callback triggered when submission fails.
   */
  onError?: (error: SubmissionError) => void;
  /**
   * Success toast message, or a function that returns one.
   */
  successMessage?: string | ((data: TResponse) => string);
  /**
   * Error toast message, or a function that returns one.
   */
  errorMessage?: string | ((error: any) => string);
}
export function useFormSubmission<TFieldValues extends FieldValues = any, TData = any, TResponse = any>({
  endpoint,
  setError,
  onSuccess,
  onError,
  successMessage = "Form submitted successfully!",
  errorMessage = "Failed to submit. Please try again."
}: UseFormSubmissionOptions<TFieldValues, TResponse> = {}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const utmParams = useUtmParams();

  // Fallback to NEXT_PUBLIC_BACKEND_URL/api/enquiry-details if not specified
  const targetUrl = endpoint || `${process.env.NEXT_PUBLIC_BACKEND_URL || ""}/api/enquiry-details`;
  const submit = async (data: TData) => {
    setIsSubmitting(true);

    // Merge active UTM parameters into the payload
    const activeUtmParams = Object.fromEntries(Object.entries(utmParams).filter(([, val]) => val !== undefined));
    const payload = typeof data === "object" && data !== null ? {
      ...data,
      ...activeUtmParams,
      clientEnquirySource: typeof window !== "undefined" ? window.location.href : ""
    } : data;
    const promise = new Promise<TResponse>(async (resolve, reject) => {
      try {
        const isFormData = typeof payload === "object" && payload !== null && Object.values(payload).some(value => value instanceof File || Array.isArray(value) && value.some(v => v instanceof File));
        let body: any;
        const headers: Record<string, string> = {
          Accept: "application/json"
        };
        if (isFormData) {
          const formData = new FormData();
          Object.entries(payload as Record<string, any>).forEach(([key, val_0]) => {
            if (val_0 instanceof File) {
              formData.append(key, val_0);
            } else if (typeof val_0 === "boolean") {
              formData.append(key, val_0 ? "1" : "0");
            } else if (Array.isArray(val_0)) {
              val_0.forEach(item => {
                if (item instanceof File) {
                  formData.append(key, item);
                } else if (typeof item === "boolean") {
                  formData.append(key, item ? "1" : "0");
                } else {
                  formData.append(key, String(item));
                }
              });
            } else if (val_0 !== undefined && val_0 !== null) {
              formData.append(key, String(val_0));
            }
          });
          body = formData;
        } else {
          headers["Content-Type"] = "application/json";
          body = JSON.stringify(payload);
        }
        const response = await fetch(targetUrl, {
          method: "POST",
          headers,
          body
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          const error_0: SubmissionError = new Error(errData.message || `HTTP error! Status: ${response.status}`);
          if (errData.errors) {
            error_0.validationErrors = errData.errors;
          }
          throw error_0;
        }
        const resData = await response.json();
        resolve(resData);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
    toast.promise(promise, {
      loading: "Submitting details...",
      success: (data_0: TResponse) => {
        const msg = typeof successMessage === "function" ? successMessage(data_0) : successMessage;
        onSuccess?.(data_0);
        return msg;
      },
      error: (err: SubmissionError) => {
        // Map backend validation errors to fields if setError is provided
        if (setError && err.validationErrors) {
          Object.entries(err.validationErrors).forEach(([key_0, val_1]) => {
            const message = Array.isArray(val_1) ? val_1[0] : val_1 as string;
            setError(key_0 as any, {
              type: "server",
              message
            });
          });
        }
        const msg_0 = typeof errorMessage === "function" ? errorMessage(err) : err.message || errorMessage;
        onError?.(err);
        return msg_0;
      }
    });
    try {
      const result = await promise;
      return {
        success: true,
        data: result
      };
    } catch (error_1) {
      return {
        success: false,
        error: error_1
      };
    } finally {
      setIsSubmitting(false);
    }
  };
  return {
    submit,
    isSubmitting
  };
}